/**
 * RoleHandoffInbox — shared command-center surface for role-routed hand-offs.
 *
 * Phase 1: Visibility & routing via `handoffRouting`.
 * Phase 2: Executable workflow cards via `handoffActions` + `runAction`.
 *
 * Design principles:
 *  - ONE execution primitive: every primary CTA flows through `runAction()`
 *    so hand-offs inherit the same modal/navigate/direct semantics used
 *    elsewhere in Koredor (Operations Command Center, Dispatch Intel,
 *    Next-Step prompts).
 *  - ONE source of truth for visibility (`handoffRouting`) and ONE for
 *    action resolution (`handoffActions`). Dashboards must not branch on
 *    intent or alias strings.
 *  - Small-op friendly: each card has one obvious next move with smart
 *    inferred CTA. No taxonomy required of the operator.
 *  - Enterprise-ready rails: structured completion outcomes, source
 *    linkage, and intent labels feed future SLA/escalation/audit work.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { Inbox, UserCheck, Briefcase, AlertTriangle, FileText, ArrowRight, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { getHandoffsForViewer } from "@/data/handoffRouting";
import {
  resolveHandoffAction,
  applyCompletionOutcome,
  getEffectiveIntent,
  getSourceLink,
  INTENT_LABELS,
  type HandoffActionRunners,
} from "@/data/handoffActions";
import { runAction } from "@/lib/actionRunner";
import { PushHandoffPrompt, type PushHandoffSelection } from "@/components/quick-actions/PushHandoffPrompt";
import { buildPushHandoff, defaultPushHandoffDueDate } from "@/data/pushHandoffEngine";
import type { FollowUpTask } from "@/data/opportunityTypes";
import { useActionQueueStore } from "@/data/actionQueue/context";

interface RoleHandoffInboxProps {
  /** Optional cap on items shown inline. Default 6. */
  maxItems?: number;
  /** Optional hide-when-empty toggle. Default false (we show empty state). */
  hideWhenEmpty?: boolean;
  /**
   * Optional modal runners. When provided and the resolved intent matches,
   * the CTA opens the relevant modal in-context instead of navigating away.
   * Hosts that don't pass runners get safe navigation fallback.
   */
  runners?: HandoffActionRunners;
}

export function RoleHandoffInbox({ maxItems = 6, hideWhenEmpty = false, runners }: RoleHandoffInboxProps) {
  const { followUpTasks, setFollowUpTasks, jobs, issues } = useAppData();
  const { role, roleInfo } = useRole();
  const { createItem: createQueueItem } = useActionQueueStore();
  const navigate = useNavigate();

  // "Complete & hand off again" prefill state.
  const [chainTask, setChainTask] = useState<FollowUpTask | null>(null);

  const viewer = useMemo(() => ({ role, label: roleInfo.label }), [role, roleInfo.label]);
  const handoffs = useMemo(
    () => getHandoffsForViewer(viewer, followUpTasks),
    [viewer, followUpTasks],
  );

  const completeHandoff = (id: string) => {
    setFollowUpTasks((prev) =>
      prev.map((t) => (t.id === id ? applyCompletionOutcome(t, "completed") : t)),
    );
    toast.success("Hand-off completed");
  };

  const completeAndChain = (task: FollowUpTask) => {
    // Mark current as completed-and-chained, then open prompt prefilled
    // for the same job.
    setFollowUpTasks((prev) =>
      prev.map((t) => (t.id === task.id ? applyCompletionOutcome(t, "completed_and_created_followup") : t)),
    );
    setChainTask(task);
  };

  const handlePrimary = (task: FollowUpTask) => {
    const action = resolveHandoffAction(task, { navigate, jobs, issues, runners });
    runAction(action, {
      navigate,
      onError: () => toast.error("Couldn't open that action — try the source link."),
    }).then((handled) => {
      if (!handled) {
        // Final safety net — open the source object directly.
        const link = getSourceLink(task);
        if (link) navigate(link.href);
      }
    });
  };

  const handleChainConfirm = (selection: PushHandoffSelection, dueIso: string) => {
    if (!chainTask) return;
    const job = jobs.find((j) => j.id === chainTask.jobId);
    if (!job) {
      toast.error("Source job not found — could not chain hand-off.");
      setChainTask(null);
      return;
    }
    const result = buildPushHandoff({
      job,
      context: {
        reasonFragment: `chained from ${chainTask.id}`,
        sourceLabel: chainTask.notes?.slice(0, 80),
      },
      assignee: selection.label,
      assigneeRoleKey: selection.roleKey,
      assigneeUserId: selection.userId,
      dueDate: dueIso,
      initiatedBy: roleInfo.label,
      intent: selection.intent,
      // Phase-3 lineage — explicit, not buried in reason text.
      originHandoffId: chainTask.id,
      workflowSourceType: "chained",
      workflowSourceId: chainTask.id,
    });
    setFollowUpTasks((prev) => [...prev, result.task]);

    // Phase 4: chained hand-offs also produce a persisted Action Queue item
    // representing the downstream work. Phase 5: link both sides bidirectionally
    // so queue completion can deterministically resolve the spawned hand-off.
    const queueItem = createQueueItem({
      title: `${result.task.customerName || "Hand-off"} — ${selection.intent ?? "follow-up"}`,
      summary: result.task.notes,
      priority: result.task.priority ?? "medium",
      assignedToRoleKey: selection.roleKey,
      assignedToUserId: selection.userId,
      assignedToLabel: selection.label,
      intent: selection.intent,
      sourceType: "chained",
      sourceId: chainTask.id,
      workflowSourceType: "chained",
      workflowSourceId: chainTask.id,
      originHandoffId: chainTask.id,
      // Phase 5 — bidirectional link: the new queue item points at the
      // *spawned* hand-off (result.task.id), which is the active routing
      // record for downstream owners. The original chainTask is now closed.
      linkedHandoffId: result.task.id,
      jobId: chainTask.jobId,
      issueId: chainTask.issueId,
      estimateId: chainTask.estimateId,
      createdBy: roleInfo.label,
    });

    // Backfill `linkedQueueItemId` on the spawned hand-off so the
    // convergence selectors find it from either direction.
    setFollowUpTasks((prev) =>
      prev.map((t) =>
        t.id === result.task.id ? { ...t, linkedQueueItemId: queueItem.id } : t,
      ),
    );

    toast.success(`Hand-off chained to ${selection.label}`);
    setChainTask(null);
  };

  if (hideWhenEmpty && handoffs.length === 0) return null;

  return (
    <>
      <Card className="border-warning/30 bg-warning/[0.03] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-bold flex items-center gap-2">
            <Inbox className="h-4 w-4 text-warning" />
            Action Required
            <Badge variant="outline" className="ml-1 text-[10px] font-mono">
              {handoffs.length}
            </Badge>
            <span className="ml-2 text-[11px] font-normal text-muted-foreground">
              Hand-offs assigned to {roleInfo.label}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-1">
          {handoffs.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">
              No outstanding hand-offs. New tasks routed to{" "}
              <span className="font-medium text-foreground">{roleInfo.label}</span> will appear here.
            </p>
          ) : (
            <div className="space-y-1.5">
              {handoffs.slice(0, maxItems).map((t) => {
                const due = new Date(t.dueDate);
                const overdue = due.getTime() < Date.now();
                const intent = getEffectiveIntent(t);
                const action = resolveHandoffAction(t, { navigate, jobs, issues, runners });
                const source = getSourceLink(t);
                const SourceIcon = t.issueId ? AlertTriangle : t.estimateId ? FileText : Briefcase;
                const canChain = !!t.jobId;
                return (
                  <div
                    key={t.id}
                    className="flex items-start gap-3 p-2.5 rounded border bg-card hover:bg-muted/30 transition-colors"
                  >
                    <div className="h-7 w-7 rounded-md bg-warning/10 flex items-center justify-center shrink-0 mt-0.5">
                      <UserCheck className="h-3.5 w-3.5 text-warning" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[12px] font-semibold text-foreground truncate">
                          {t.customerName || "Hand-off"}
                        </span>
                        <Badge variant="secondary" className="text-[10px] font-medium">
                          {INTENT_LABELS[intent]}
                        </Badge>
                        {source && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
                            <SourceIcon className="h-3 w-3" />
                            {source.refId}
                          </span>
                        )}
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            overdue
                              ? "border-destructive/40 text-destructive bg-destructive/5"
                              : "border-warning/40 text-warning bg-warning/5"
                          }`}
                        >
                          {overdue ? "Overdue" : "Due"} {format(due, "MMM d")}
                        </Badge>
                        {t.priority === "high" && (
                          <Badge variant="outline" className="text-[10px] border-destructive/30 text-destructive">
                            High
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                        {t.notes}
                      </p>
                      {t.assignedToLabel && t.assignedToLabel !== roleInfo.label && (
                        <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                          Routed to: {t.assignedToLabel}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {/* Primary CTA — always present, resolved centrally */}
                      <button
                        onClick={() => handlePrimary(t)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                      >
                        {action.label}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                      <div className="flex items-center gap-1.5">
                        {canChain && (
                          <button
                            onClick={() => completeAndChain(t)}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                            title="Mark complete and create a new hand-off for the next owner"
                          >
                            <RotateCcw className="h-2.5 w-2.5" />
                            Complete &amp; chain
                          </button>
                        )}
                        <button
                          onClick={() => completeHandoff(t.id)}
                          className="text-[10px] px-2 py-1 rounded border border-success/40 text-success bg-success/5 hover:bg-success/10 transition-colors"
                        >
                          Complete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {handoffs.length > maxItems && (
                <Link
                  to="/follow-ups"
                  className="block text-[11px] text-primary hover:underline pt-1"
                >
                  View all {handoffs.length} hand-offs →
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Complete-and-chain prompt — reuses PushHandoffPrompt for consistency */}
      <Dialog open={!!chainTask} onOpenChange={(o) => !o && setChainTask(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Chain hand-off — pick next owner</DialogTitle>
          </DialogHeader>
          {chainTask && (
            <PushHandoffPrompt
              defaultAssignee={roleInfo.label}
              defaultDueDate={defaultPushHandoffDueDate()}
              reasonFragment={`continued from ${chainTask.customerName || chainTask.id}`}
              defaultIntent={getEffectiveIntent(chainTask)}
              onCancel={() => setChainTask(null)}
              onConfirm={() => { /* handled by structured callback */ }}
              onConfirmStructured={handleChainConfirm}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
